import { useState } from "react";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { Link } from "../router-compat";
import { useGetApiV1Attention } from "@/shared/api/generated/attention/attention";
import { useLocale } from "@/shared/lib/i18n/locale-context";
import { formatElapsed } from "@/shared/lib/format/dates";
import { LiveEvery } from '@/shared/lib/live';

/**
 * The bell: what is waiting on the control room right now (request of 13 September 2026).
 *
 * ⚠️⚠️ **Until 13 September 2026 this was TailAdmin's demo list** — Terry Franci asking for
 * permission on «Project - Nganter App», on the production panel of a search-and-rescue association.
 * An administrator opened it looking for something that needed them.
 *
 * ⚠️ **Derived from state, so nothing is "marked read".** The server answers what still needs
 * somebody — a find on a search that is still open, a volunteer with a document and no decision, a
 * mission waiting to be submitted, voted or published — and an item leaves the list when the thing
 * behind it is done. The dot is the count of those, not of unseen messages.
 *
 * Re-read every twenty seconds: a find from the field is the one item that must not wait for a
 * page reload — and a minute, which this was, is a long time to be the last to know.
 */
export default function NotificationDropdown() {
  const { t, locale } = useLocale();
  const [isOpen, setIsOpen] = useState(false);

  const attention = useGetApiV1Attention({
    query: { refetchInterval: LiveEvery.queue, refetchOnWindowFocus: true },
  });

  const data = attention.data;
  const finds = data?.finds ?? [];
  const missions = data?.missions ?? [];
  const volunteers = data?.volunteersAwaitingReview ?? 0;
  const count = finds.length + missions.length + (volunteers > 0 ? 1 : 0);

  const close = () => setIsOpen(false);

  const conditionLabel = (condition: string | null | undefined) =>
    condition && condition in t.attention.condition
      ? t.attention.condition[condition as keyof typeof t.attention.condition]
      : t.attention.condition.unknown;

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={count > 0 ? `${t.attention.open} (${count})` : t.attention.open}
        className="relative flex items-center justify-center text-gray-500 transition-colors bg-white border border-gray-200 rounded-full dropdown-toggle hover:text-gray-700 h-11 w-11 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        {count > 0 && (
          <span
            data-testid="attention-count"
            className={`absolute -end-1 -top-1 z-10 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-semibold text-white ${
              finds.length > 0 ? "bg-error-600" : "bg-warning-500"
            }`}
          >
            {count}
          </span>
        )}
        <svg className="fill-current" width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.75 2.29248C10.75 1.87827 10.4143 1.54248 10 1.54248C9.58583 1.54248 9.25004 1.87827 9.25004 2.29248V2.83613C6.08266 3.20733 3.62504 5.9004 3.62504 9.16748V14.4591H3.33337C2.91916 14.4591 2.58337 14.7949 2.58337 15.2091C2.58337 15.6234 2.91916 15.9591 3.33337 15.9591H4.37504H15.625H16.6667C17.0809 15.9591 17.4167 15.6234 17.4167 15.2091C17.4167 14.7949 17.0809 14.4591 16.6667 14.4591H16.375V9.16748C16.375 5.9004 13.9174 3.20733 10.75 2.83613V2.29248ZM14.875 14.4591V9.16748C14.875 6.47509 12.6924 4.29248 10 4.29248C7.30765 4.29248 5.12504 6.47509 5.12504 9.16748V14.4591H14.875ZM8.00004 17.7085C8.00004 18.1228 8.33583 18.4585 8.75004 18.4585H11.25C11.6643 18.4585 12 18.1228 12 17.7085C12 17.2943 11.6643 16.9585 11.25 16.9585H8.75004C8.33583 16.9585 8.00004 17.2943 8.00004 17.7085Z"
            fill="currentColor"
          />
        </svg>
      </button>
      <Dropdown
        isOpen={isOpen}
        onClose={close}
        className="absolute -end-[240px] mt-[17px] flex max-h-[480px] w-[350px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark sm:w-[361px] lg:end-0"
      >
        <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100 dark:border-gray-700">
          <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{t.attention.title}</h5>
          <button
            type="button"
            aria-label={t.attention.close}
            onClick={close}
            className="text-gray-500 transition dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        <ul className="flex flex-col h-auto gap-1 overflow-y-auto custom-scrollbar">
          {attention.isError && (
            <li className="px-3 py-4 text-sm text-error-600">{t.attention.failed}</li>
          )}

          {!attention.isError && data && count === 0 && (
            <li className="px-3 py-6 text-sm text-center text-gray-500 dark:text-gray-400">{t.attention.nothing}</li>
          )}

          {/* ⚠️ Finds first, in red: a person found and possibly needing an ambulance outranks every queue. */}
          {finds.map((find) => (
            <li key={`find-${find.missionId}-${find.reportedAt}`}>
              <Link
                to={`/missions/${find.missionId}`}
                onClick={close}
                className="flex flex-col gap-0.5 rounded-lg border-s-4 border-error-600 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-white/5"
              >
                <span className="text-sm font-semibold text-error-600 dark:text-error-500">
                  {t.attention.foundTitle} — {conditionLabel(find.condition)}
                </span>
                <span className="text-sm text-gray-800 dark:text-gray-200">{find.missionTitle}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {find.reporterName ? `${t.attention.foundBy} ${find.reporterName} · ` : ""}
                  {formatElapsed(find.reportedAt, locale)}
                </span>
              </Link>
            </li>
          ))}

          {volunteers > 0 && (
            <li>
              <Link
                to="/volunteers"
                onClick={close}
                className="flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-white/5"
              >
                <span className="text-sm text-gray-800 dark:text-gray-200">{t.attention.volunteers}</span>
                <span className="rounded-full bg-warning-50 px-2 py-0.5 text-xs font-semibold text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                  {volunteers}
                </span>
              </Link>
            </li>
          )}

          {missions.map((mission) => (
            <li key={`mission-${mission.missionId}`}>
              <Link
                to={`/missions/${mission.missionId}`}
                onClick={close}
                className="flex flex-col gap-0.5 rounded-lg px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-white/5"
              >
                <span className="text-sm text-gray-800 dark:text-gray-200">{mission.title}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t.attention.missionWaiting} · {t.missionStatus[mission.status]} · {formatElapsed(mission.since, locale)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Dropdown>
    </div>
  );
}
